import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Wand2, Sparkles } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const AIAssistant = () => {
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [recommendation, setRecommendation] = useState("");
  const { toast } = useToast();

  const getRecommendation = async () => {
    if (!query.trim()) {
      toast({
        title: "Please enter a query",
        description: "Tell us what kind of tool you're looking for",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: { query, type: 'recommend' },
      });

      if (error) throw error;
      setRecommendation(data.response);
      
      toast({
        title: "AI Assistant",
        description: "Generated recommendations based on your query",
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to get recommendations. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      getRecommendation();
    }
  };

  return (
    <Card className="bg-gradient-to-br from-purple-50 to-white border border-purple-100/50 shadow-lg transition-all duration-300 hover:shadow-xl">
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <div className="relative">
          <Wand2 className="h-5 w-5 text-purple-500" />
          <Sparkles className="h-3 w-3 text-yellow-500 absolute -top-1 -right-1 animate-pulse" />
        </div>
        <h2 className="text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-purple-900">
          AI Assistant
        </h2>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="What kind of tool are you looking for?"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            className="flex-1 border-purple-100 focus:border-purple-200 focus:ring-purple-100"
          />
          <Button
            onClick={getRecommendation}
            disabled={isLoading}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-md hover:shadow-lg"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Thinking...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                <span>Ask AI</span>
              </div>
            )}
          </Button>
        </div>
        {recommendation && (
          <div className="p-4 bg-gradient-to-br from-purple-50/50 to-white rounded-lg border border-purple-100/30 shadow-inner">
            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{recommendation}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIAssistant;
